import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDogs } from '../hooks/useDogs';
import { useAuth } from '../context/AuthContext';
import { DaycareService } from '../services/daycareService';
import { normalizeText } from '../utils/dataUtils';

const CheckInWizard = ({ visits = [], loading, onSubmit, onCancel }) => {
    const { dogs, loading: dogsLoading, loadDogs } = useDogs();

    useEffect(() => {
        if (loadDogs) {
            loadDogs({ limit: 1000 });
        }
    }, [loadDogs]);
    const { authorizedFetch } = useAuth();
    const navigate = useNavigate();

    const [step, setStep] = useState(1);
    const [pickedDog, setPickedDog] = useState(null);
    const [pickedPackage, setPickedPackage] = useState(null);
    const [visitType, setVisitType] = useState('regular');
    const [notes, setNotes] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [dogPackages, setDogPackages] = useState([]);
    const [packagesLoading, setPackagesLoading] = useState(false);
    // dogIds selected for group check-in — keyed by dog._id
    const [groupSelection, setGroupSelection] = useState({});

    const checkedInIds = useMemo(() =>
        new Set(visits.filter(v => v.checkInTime && !v.checkOutTime).map(v => v.dog?._id).filter(Boolean)),
        [visits]
    );

    const availableDogs = useMemo(() => {
        const term = normalizeText(searchTerm);
        return dogs
            .filter(d => !checkedInIds.has(d._id))
            .filter(d =>
                !term ||
                normalizeText(d.name).includes(term) ||
                normalizeText(d.breed).includes(term) ||
                d.owners?.some(o => normalizeText(o.name).includes(term))
            );
    }, [dogs, checkedInIds, searchTerm]);

    // Fetch packages when dog is picked — include shared packages
    useEffect(() => {
        if (!pickedDog || !authorizedFetch) return;
        setPackagesLoading(true);
        setDogPackages([]);
        setPickedPackage(null);
        setGroupSelection({});
        DaycareService.getActivePackages(authorizedFetch)
            .then(pkgs => {
                const relevant = pkgs.filter(p => {
                    const primaryId = p.dog?._id || p.dog;
                    const sharedIds = (p.sharedDogs || []).map(d => d._id || d);
                    return primaryId === pickedDog._id || sharedIds.includes(pickedDog._id);
                });
                setDogPackages(relevant);
                // With one package there is nothing to choose: pre-select it so
                // the review step shows the real balance and the check-in charges
                // the package on screen instead of falling back to oldest-first.
                if (relevant.length === 1) setPickedPackage(relevant[0]);
            })
            .catch(() => setDogPackages([]))
            .finally(() => setPackagesLoading(false));
    }, [pickedDog, authorizedFetch]);

    // When package selection changes, reset group selection
    useEffect(() => {
        if (!pickedDog) return;
        setGroupSelection({ [pickedDog._id]: true });
    }, [pickedPackage, pickedDog]);

    const pickDog = (dog) => {
        setPickedDog(dog);
        setGroupSelection({ [dog._id]: true });
        setStep(2);
    };

    // Shared dogs in the selected package, excluding already checked-in dogs
    const sharedDogsInPackage = useMemo(() => {
        if (!pickedPackage) return [];
        return (pickedPackage.sharedDogs || []).filter(d => {
            const id = d._id || d;
            return id !== pickedDog?._id && !checkedInIds.has(id);
        });
    }, [pickedPackage, pickedDog, checkedInIds]);

    const toggleGroupDog = (dogId) => {
        setGroupSelection(prev => ({ ...prev, [dogId]: !prev[dogId] }));
    };

    const selectedDogIds = Object.keys(groupSelection).filter(id => groupSelection[id]);

    const handleConfirm = () => {
        onSubmit({
            dogIds: selectedDogIds,
            notes,
            packageId: (visitType === 'trial' || visitType === 'single') ? '' : (pickedPackage?._id || ''),
            visitType,
        });
    };

    const now = new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    const ownerNames = pickedDog?.owners?.map(o => o.name).join(', ') || '—';

    // Look up dog objects for the review step
    const selectedDogObjects = useMemo(() =>
        selectedDogIds.map(id => dogs.find(d => d._id === id)).filter(Boolean),
        [selectedDogIds, dogs]
    );

    return (
        <div className="ci-wizard">
            {/* Progress */}
            <div className="ci-progress-wrap">
                <p className="ci-step-label">Paso {step} de 3</p>
                <div className="ci-progress">
                    {[1, 2, 3].map(n => (
                        <div key={n} className={`ci-progress-bar${step >= n ? ' on' : ''}`} />
                    ))}
                </div>
            </div>

            {/* ── STEP 1: Pick a dog ── */}
            {step === 1 && (
                <div className="ci-step-body">
                    <h2 className="ci-step-title">Seleccionar perro</h2>
                    <div className="ci-search-wrap">
                        <span className="ci-search-icon">🔍</span>
                        <input
                            className="ci-search"
                            placeholder="Buscar por nombre, raza o propietario…"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            autoFocus
                        />
                    </div>
                    {dogsLoading ? (
                        <p className="ci-loading">Cargando perros…</p>
                    ) : availableDogs.length === 0 ? (
                        <p className="ci-empty">
                            {searchTerm ? 'Ningún perro coincide con tu búsqueda.' : 'Todos los perros ya tienen entrada hoy.'}
                        </p>
                    ) : (
                        <div className="ci-dog-list">
                            {availableDogs.map(dog => (
                                <button
                                    key={dog._id}
                                    className="ci-dog-card"
                                    onClick={() => pickDog(dog)}
                                >
                                    <div className="ci-dog-avatar">
                                        {dog.pictureUrl || dog.picture
                                            ? <img src={dog.pictureUrl || dog.picture} alt={dog.name} />
                                            : <span>🐶</span>
                                        }
                                    </div>
                                    <div className="ci-dog-info">
                                        <span className="ci-dog-name">{dog.name}</span>
                                        <span className="ci-dog-meta">{dog.breed}{dog.owners?.[0] ? ` · ${dog.owners[0].name}` : ''}</span>
                                    </div>
                                    <span className="ci-dog-chevron">›</span>
                                </button>
                            ))}
                        </div>
                    )}
                    <div className="ci-actions">
                        <button className="btn-secondary" onClick={onCancel}>Cancelar</button>
                    </div>
                </div>
            )}

            {/* ── STEP 2: Package ── */}
            {step === 2 && pickedDog && (
                <div className="ci-step-body">
                    <div className="ci-selected-bar">
                        <div className="ci-dog-avatar">
                            {pickedDog.pictureUrl || pickedDog.picture
                                ? <img src={pickedDog.pictureUrl || pickedDog.picture} alt={pickedDog.name} />
                                : <span>🐶</span>
                            }
                        </div>
                        <div className="ci-dog-info">
                            <span className="ci-dog-name">{pickedDog.name}</span>
                            <span className="ci-dog-meta">{ownerNames}</span>
                        </div>
                        <button className="ci-change-btn" onClick={() => { setStep(1); setPickedDog(null); }}>
                            Cambiar
                        </button>
                    </div>

                    <h2 className="ci-step-title">Paquete</h2>
                    {packagesLoading ? (
                        <p className="ci-loading">Cargando paquetes…</p>
                    ) : (
                        <>
                        {!packagesLoading && dogPackages.length === 0 && visitType === 'regular' && (
                            <div className="ci-no-package-warning">
                                ⚠️ Este perro no tiene un paquete activo. Selecciona Día suelto o registra un paquete antes de hacer el check-in.
                            </div>
                        )}
                        <div className="ci-pkg-grid">
                            {dogPackages.map(pkg => (
                                <button
                                    key={pkg._id}
                                    className={`ci-pkg-chip${pickedPackage?._id === pkg._id ? ' on' : ''}`}
                                    onClick={() => { setPickedPackage(pkg); setVisitType('regular'); }}
                                >
                                    <span className="ci-pkg-name">{pkg.packageType}</span>
                                    <span className="ci-pkg-sub">{pkg.daysLeft} día{pkg.daysLeft !== 1 ? 's' : ''} restante{pkg.daysLeft !== 1 ? 's' : ''}</span>
                                    {(pkg.sharedDogs?.length > 0) && (
                                        <span className="ci-pkg-shared-badge">
                                            {pkg.sharedDogs.length + 1} perros
                                        </span>
                                    )}
                                </button>
                            ))}
                            <button
                                className={`ci-pkg-chip ci-pkg-chip--trial${visitType === 'single' ? ' on' : ''}`}
                                onClick={() => { setPickedPackage(null); setVisitType('single'); }}
                            >
                                <span className="ci-pkg-name">🎟️ Día suelto</span>
                                <span className="ci-pkg-sub">Sin cobro de paquete</span>
                            </button>
                        </div>
                        <button
                            className="ci-add-package-link"
                            onClick={() => navigate('/daycare/packages')}
                        >
                            + Agregar paquete a este perro
                        </button>
                        </>
                    )}

                    {/* Group dogs checkbox when a shared package is selected */}
                    {pickedPackage && sharedDogsInPackage.length > 0 && (
                        <div className="ci-group-section">
                            <p className="ci-group-title">¿Quién más entra hoy?</p>
                            <p className="ci-group-subtitle">
                                {pickedDog.name} ya está incluido. Selecciona los demás.
                            </p>
                            <div className="ci-group-list">
                                {sharedDogsInPackage.map(sd => {
                                    const sdId = sd._id || sd;
                                    const sdDog = dogs.find(d => d._id === sdId);
                                    const checked = !!groupSelection[sdId];
                                    return (
                                        <label key={sdId} className="ci-group-row">
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={() => toggleGroupDog(sdId)}
                                            />
                                            <div className="ci-dog-avatar ci-dog-avatar--sm">
                                                {sdDog?.pictureUrl || sdDog?.picture
                                                    ? <img src={sdDog.pictureUrl || sdDog.picture} alt={sdDog.name} />
                                                    : <span>🐶</span>
                                                }
                                            </div>
                                            <div className="ci-dog-info">
                                                <span className="ci-dog-name">{sd.name || sdDog?.name || sdId}</span>
                                                {sdDog?.breed && <span className="ci-dog-meta">{sdDog.breed}</span>}
                                            </div>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <div className="ci-actions">
                        <button className="btn-secondary" onClick={() => setStep(1)}>Atrás</button>
                        <button
                            className="btn-primary"
                            onClick={() => setStep(3)}
                            disabled={!packagesLoading && dogPackages.length === 0 && visitType === 'regular'}
                        >Continuar</button>
                    </div>
                </div>
            )}

            {/* ── STEP 3: Review & confirm ── */}
            {step === 3 && pickedDog && (
                <div className="ci-step-body">
                    <h2 className="ci-step-title">Confirmar</h2>

                    <div className="ci-review-card">
                        <div className="ci-kv">
                            <span className="ci-k">Perro{selectedDogObjects.length > 1 ? 's' : ''}</span>
                            <span className="ci-v">
                                {selectedDogObjects.map(d => d.name).join(', ')}
                            </span>
                        </div>
                        <div className="ci-kv"><span className="ci-k">Propietario</span><span className="ci-v">{ownerNames}</span></div>
                        <div className="ci-kv">
                            <span className="ci-k">Paquete</span>
                            <span className="ci-v">
                                {visitType === 'trial'
                                    ? '🎟️ Día de Prueba · sin cobro'
                                    : visitType === 'single'
                                        ? '🎟️ Día suelto · con cobro'
                                        : pickedPackage
                                            ? `${pickedPackage.packageType} · ${pickedPackage.daysLeft} días restantes → ${pickedPackage.daysLeft - selectedDogIds.length} tras esta entrada`
                                            : 'Sin paquete'}
                            </span>
                        </div>
                        <div className="ci-kv"><span className="ci-k">Hora entrada</span><span className="ci-v">{now}</span></div>
                    </div>

                    <div className="ci-notes-wrap">
                        <label className="ci-notes-label">Notas para el personal</label>
                        <textarea
                            className="ci-notes"
                            rows={3}
                            placeholder="¿Algo que indicar? Ánimo, comida traída, cuidados especiales…"
                            value={notes}
                            onChange={e => setNotes(e.target.value)}
                        />
                    </div>

                    <div className="ci-actions">
                        <button className="btn-secondary" onClick={() => setStep(2)}>Atrás</button>
                        <button
                            className="btn-primary"
                            onClick={handleConfirm}
                            disabled={loading}
                        >
                            {loading ? 'Registrando…' : `Confirmar entrada${selectedDogIds.length > 1 ? ` (${selectedDogIds.length})` : ''}`}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CheckInWizard;
